// Single source of truth for "what URL do we put on the wire when a
// user clicks Share?" Used by both the matchup-page ShareButton and the
// home-feed HomeCard so iMessage / Slack / X get the same crawler-aware
// preview URL regardless of where the user hit Share.
//
// On Render, the API + static-site live on different .onrender.com
// subdomains and the static site CAN'T proxy /m/<short> to the API
// (cross-origin destinations in _redirects are silently dropped). So
// short-URL share links must land on the API origin or crawlers see
// the SPA's marketing card. The long-URL fallbacks stay on the SPA
// origin because those are React Router routes the API doesn't serve.

import { API_BASE_URL } from '../services/api';

/**
 * Build the canonical share URL for a matchup or bracket card.
 *
 * @param {object} args
 * @param {object} args.item          Matchup or bracket record. Must
 *                                    expose `id` (long UUID) and ideally
 *                                    `short_id`. Author hints (`author`,
 *                                    `author_username`, `author_id`) are
 *                                    used for the long-URL fallback only.
 * @param {'matchup'|'bracket'} args.type
 * @param {string} [args.origin]      SPA origin for fallback paths.
 *                                    Defaults to window.location.origin.
 * @returns {string} Absolute share URL.
 */
export function buildShareUrl({ item, type, origin }) {
  const spaOrigin =
    origin || (typeof window !== 'undefined' ? window.location.origin : '');
  // Trim trailing slash so concatenation produces clean URLs even when
  // API_BASE_URL is the empty string (K8s same-host deploy).
  const apiOrigin = (API_BASE_URL || spaOrigin).replace(/\/$/, '');
  const shortID = item?.short_id ?? item?.shortId;

  // Tier lists + pools have no crawler-prefixed short link — route to
  // their SPA pages directly so Share doesn't fall through to the /m/
  // matchup prefix (which would build a broken link).
  if (type === 'tierlist') {
    return `${spaOrigin}/tierlists/${item.id}`;
  }
  if (type === 'pool') {
    return `${spaOrigin}/pools/${item.id}`;
  }

  if (shortID) {
    const prefix = type === 'bracket' ? '/b/' : '/m/';
    return `${apiOrigin}${prefix}${shortID}`;
  }

  // Fallback — long UUID URL. Shareable but the crawler preview will
  // only work for the SPA-crawler middleware case (Phase 2 OG meta on
  // long URLs). New matchups always have short_id; this branch is for
  // pre-backfill rows.
  if (type === 'bracket') {
    return `${spaOrigin}/brackets/${item.id}`;
  }
  const uid =
    item.author?.username ??
    item.author_username ??
    item.authorUsername ??
    item.author_id ??
    item.authorId ??
    'u';
  return `${spaOrigin}/users/${uid}/matchup/${item.id}`;
}

/**
 * Append `?ref=<channel>` to a share URL so the API's
 * /share/landed beacon can attribute the click. Preserves any existing
 * query string the caller built (e.g. cache-buster).
 */
export function withRef(url, ref) {
  if (!ref) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}ref=${encodeURIComponent(ref)}`;
}
